/**
 * create_job input schema + exposure guard.
 *
 * The MCP `create_job` tool advertises a *curated* subset of the canonical
 * {@link CreateTestJobRequest} contract — rich, LLM-facing descriptions, with
 * internal/deprecated/un-advertised fields deliberately hidden. Historically
 * that subset was hand-maintained and silently drifted behind the contract as
 * new options shipped (#5959).
 *
 * Two compile-time guards make that drift impossible from here on:
 *
 *  1. {@link CREATE_JOB_EXPOSURE} `satisfies Record<keyof CreateTestJobRequest,
 *     ExposureDecision>` — every contract field MUST be classified as either
 *     `'exposed'` or `'excluded:<reason>'`. Add a field to the contract and
 *     forget it here → `tsc` fails.
 *  2. {@link createJobProperties} is typed `Record<ExposedContractField |
 *     McpOnlyField, …>` — the advertised JSON-Schema properties are forced to
 *     be EXACTLY the exposed contract fields (plus the two MCP-only reference
 *     tags). Mark a field `'exposed'` but forget its schema entry — or leave a
 *     schema entry for a field that isn't `'exposed'` — → `tsc` fails.
 *
 * The exposure map is therefore the single source of truth for *which* fields
 * the tool offers; the properties object supplies *how* each is described.
 */

import type { CreateTestJobRequest } from '@runhuman/shared';
import { DEVICE_CAPABILITY, LANGUAGE_CAPABILITY } from '@runhuman/shared';

/** `'exposed'`, or `'excluded:<reason>'` recording WHY a contract field is hidden. */
export type ExposureDecision = 'exposed' | `excluded:${string}`;

/**
 * Classifies every field of {@link CreateTestJobRequest}. Excluded fields carry
 * an auditable reason so the decision survives future readers.
 */
export const CREATE_JOB_EXPOSURE = {
  projectId: 'exposed',
  organizationId: 'exposed',
  url: 'exposed',
  description: 'exposed',
  template: 'exposed',
  templateContent: 'exposed',
  targetDurationMinutes: 'exposed',
  maxExtensionMinutes: 'exposed',
  outputSchema: 'exposed',
  resultsTemplate: 'exposed',
  deviceClass: 'exposed',
  additionalValidationInstructions: 'exposed',
  githubRepos: 'exposed',
  githubRepo: 'exposed',
  autoCreateGithubIssuesRepo: 'exposed',
  githubToken: 'exposed',
  commitSha: 'exposed',
  enableCodeContext: 'exposed',
  prNumbers: 'exposed',
  issueNumbers: 'exposed',
  checkTestability: 'exposed',
  requiredDevices: 'exposed',
  requiredLanguages: 'exposed',
  requireSocialVideos: 'exposed',
  requireSideload: 'exposed',
  autoCreateGithubIssues: 'exposed',
  autoCreateGithubFeedback: 'exposed',
  autoCreateJiraFeedback: 'exposed',
  autoCreateJiraIssues: 'exposed',
  autoCreateLinearIssues: 'exposed',
  enhancedVideo: 'exposed',
  enhanceInstructions: 'exposed',
  autoCreateOnlyTesterSurfaced: 'exposed',
  reopenIssuesOnDuplicate: 'exposed',
  commentOnDuplicate: 'exposed',
  emailOnCompletion: 'exposed',
  inAppOnCompletion: 'exposed',
  emailOnFailure: 'exposed',
  inAppOnFailure: 'exposed',

  // Deliberately un-advertised — each with the reason it stays hidden.
  attachments: 'excluded:feature-backlogged-#3185',
  screenSize: 'excluded:deprecated-use-deviceClass',
  requiresRunhumanApkInstall: 'excluded:internal-only',
  metadata: 'excluded:synthesized-server-side',
  slackNotification: 'excluded:tester-ops-internal',
} satisfies Record<keyof CreateTestJobRequest, ExposureDecision>;

/** The contract fields classified `'exposed'` above. */
type ExposedContractField = {
  [K in keyof typeof CREATE_JOB_EXPOSURE]: (typeof CREATE_JOB_EXPOSURE)[K] extends 'exposed' ? K : never;
}[keyof typeof CREATE_JOB_EXPOSURE];

/**
 * Reference tags the MCP tool adds on top of the contract (#5932). `case`/`pr`
 * are inert tracker refs folded into `metadata.refs` by the handler.
 */
type McpOnlyField = 'case' | 'pr';

interface JsonSchemaProperty {
  readonly type: string | readonly string[];
  readonly description: string;
  readonly enum?: readonly string[];
  readonly items?: unknown;
}

/**
 * JSON-Schema advertised to MCP clients at handshake. Typed to be EXACTLY the
 * exposed contract fields plus the MCP-only reference tags — the compiler
 * rejects any field that isn't `'exposed'` and any exposed field left out.
 */
export const createJobProperties: Record<ExposedContractField | McpOnlyField, JsonSchemaProperty> = {
  projectId: {
    type: 'string',
    description: 'Project ID. Required unless organizationId + githubRepo are provided for auto-creation.',
  },
  organizationId: {
    type: 'string',
    description: 'Organization ID. Use with githubRepos to auto-create a project, or to specify billing context when using PAT auth.',
  },
  url: {
    type: 'string',
    description: 'The URL to test (must be publicly accessible). Can be omitted if using a template with a default URL or if the project has a default URL.',
  },
  description: {
    type: 'string',
    description: 'Clear instructions for the human tester. Can be omitted if using a template with test instructions.',
  },
  template: {
    type: 'string',
    description: 'Template name to use as base configuration. Template fields are applied first, then directly-provided fields override them. Use list_templates to see available templates.',
  },
  templateContent: {
    type: 'string',
    description: 'Raw template content (markdown with YAML frontmatter). Parsed server-side and merged with explicit fields. Alternative to referencing a saved template by name.',
  },
  outputSchema: {
    type: 'object',
    description: 'JSON Schema defining structured data to extract from the tester\'s response. If omitted, only success/explanation are extracted.',
  },
  resultsTemplate: {
    type: 'string',
    description: 'MDForm template for text report. AI fills this to create results.text. Alternative to outputSchema for free-form structured results.',
  },
  targetDurationMinutes: {
    type: 'number',
    description: 'Time limit in minutes for the tester. Default: 30. Range: 1-60.',
  },
  maxExtensionMinutes: {
    type: 'number',
    description: 'Total overtime budget in minutes on top of base duration. Default: 15. Set to 0 to disable extensions.',
  },
  additionalValidationInstructions: {
    type: 'string',
    description: 'Custom instructions for AI when validating test results.',
  },
  githubRepos: {
    type: 'array',
    items: { type: 'string' },
    description: 'GitHub repositories in "owner/repo" format. README.md and CLAUDE.md will be fetched from each repo for LLM context.',
  },
  githubRepo: {
    type: 'string',
    description: 'Single GitHub repo in "owner/repo" format. Use githubRepos for multiple.',
  },
  githubToken: {
    type: 'string',
    description: 'GitHub token for operations without GitHub App installation. Typically from GitHub Actions.',
  },
  commitSha: {
    type: 'string',
    description: 'Commit SHA this test is run against — recorded on the job so you can later verify which code version it exercised. Inert bookkeeping (it also drives the code-context overlay when a repo is linked).',
  },
  enableCodeContext: {
    type: 'boolean',
    description: 'Per-job opt-out for the code-context stage (file/symbol refs, root cause, suggested fix), which runs by default for allowlisted orgs when a repo is linked. Set false to skip it on this one job. Non-allowlisted orgs never get code context regardless.',
  },
  prNumbers: {
    type: 'array',
    items: { type: 'number' },
    description: 'PR numbers to test. Triggers AI-powered test plan generation from PR changes. Requires githubRepos.',
  },
  issueNumbers: {
    type: 'array',
    items: { type: 'number' },
    description: 'Issue numbers to test. Triggers AI-powered test plan generation from issue descriptions. Requires githubRepos.',
  },
  checkTestability: {
    type: 'boolean',
    description: 'Reject job early if AI determines it\'s not testable. Default: true when prNumbers/issueNumbers provided.',
  },
  deviceClass: {
    type: 'string',
    enum: ['desktop', 'mobile', 'both'],
    description: 'Device class hint for the claim surface and viewport. "desktop" (1600x900) restricts the claim to the tester portal; "mobile" (375x812) restricts to the mobile app; "both" leaves the claim surface open. Required-Devices (iOS/Android force mobile; PC/Mac force desktop) take precedence — the create-job response includes a warning when this hint conflicts with required devices. Defaults to "both".',
  },
  requiredDevices: {
    type: 'array',
    items: { type: 'string', enum: [...DEVICE_CAPABILITY.values] },
    description: 'Required device capabilities for the tester. Only available on Max/Enterprise plans.',
  },
  requiredLanguages: {
    type: 'array',
    items: { type: 'string', enum: [...LANGUAGE_CAPABILITY.values] },
    description: 'Required language capabilities for the tester. Only available on Max/Enterprise plans.',
  },
  requireSocialVideos: {
    type: 'boolean',
    description: 'Whether the tester must be able to record social-media-style video walkthroughs. Only available on Max, Enterprise, and Enterprise Pro plans — requests from lower tiers are rejected with HTTP 403 (code: SOCIAL_VIDEOS_TIER_REQUIRED).',
  },
  requireSideload: {
    type: 'boolean',
    description: 'Whether the tester must be able to sideload mobile apps (Android APK or iOS IPA). When set, exactly one of `requiredDevices: ["ios"]` or `requiredDevices: ["android"]` must be provided to identify the target platform. Only available on Max, Enterprise, and Enterprise Pro plans — requests from lower tiers are rejected with HTTP 403 (code: SIDELOAD_TIER_REQUIRED).',
  },
  autoCreateGithubIssues: {
    type: 'boolean',
    description: 'Automatically create GitHub issues from AI-extracted test findings after job completion. Requires githubRepos to be set.',
  },
  autoCreateGithubIssuesRepo: {
    type: 'string',
    description: 'Target repo for auto-created issues (format: "owner/repo"). Must be one of the githubRepos. Defaults to the first repo.',
  },
  autoCreateGithubFeedback: {
    type: 'boolean',
    description: 'Also auto-create GitHub issues from the tester\'s AI-extracted feedback bucket (suggestions/observations), not just bug findings. Pairs with autoCreateGithubIssues.',
  },
  autoCreateJiraIssues: {
    type: 'boolean',
    description: 'Automatically create Jira issues from AI-extracted findings after job completion. Requires the org\'s Jira integration to be connected.',
  },
  autoCreateJiraFeedback: {
    type: 'boolean',
    description: 'Also auto-create Jira issues from the tester\'s AI-extracted feedback bucket (suggestions/observations), not just bug findings. Requires the org\'s Jira integration.',
  },
  autoCreateLinearIssues: {
    type: 'boolean',
    description: 'Automatically create Linear issues from AI-extracted findings after job completion. Requires the org\'s Linear integration to be connected.',
  },
  autoCreateOnlyTesterSurfaced: {
    type: 'boolean',
    description: 'When auto-creating issues, only file findings the tester explicitly surfaced (skip telemetry-only observations).',
  },
  reopenIssuesOnDuplicate: {
    type: 'boolean',
    description: 'When a duplicate match resolves to a closed issue, reopen it. Defaults to false: closed issues stay closed.',
  },
  commentOnDuplicate: {
    type: 'boolean',
    description: 'When a finding matches an existing issue, post a cross-reference comment on that issue. Defaults to false: re-runs do not comment on existing issues.',
  },
  enhancedVideo: {
    type: 'boolean',
    description: 'Produce a polished video alongside the raw recording: logo intro/outro, captions burned in from the tester transcript, and a corner overlay showing each issue while it is being demonstrated. Defaults to false. Rendered asynchronously after the job completes; poll the job to retrieve the URL when ready.',
  },
  enhanceInstructions: {
    type: 'boolean',
    description: 'Rewrite the tester-facing instructions using the project\'s Knowledge Base (features + user journeys) before posting, so the tester sees a feature-grounded plan instead of the bare description. Enterprise+ orgs with a successful KB augmentation run only. Defaults to false.',
  },
  emailOnCompletion: {
    type: 'string',
    enum: ['inherit', 'always', 'never'],
    description: 'Per-job email-on-completion override. Default `inherit` defers to the project tier, then to the user\'s account-level Job updates preference. `always` forces a completion email regardless; `never` suppresses it regardless.',
  },
  inAppOnCompletion: {
    type: 'string',
    enum: ['inherit', 'always', 'never'],
    description: 'Per-job in-app (dashboard inbox) notification-on-completion override. In-app twin of emailOnCompletion. Default `inherit`.',
  },
  emailOnFailure: {
    type: 'string',
    enum: ['inherit', 'always', 'never'],
    description: 'Per-job email-on-failure override — sends an email when the job ends in a terminal failure (expired/failed/cancelled). Default `inherit`.',
  },
  inAppOnFailure: {
    type: 'string',
    enum: ['inherit', 'always', 'never'],
    description: 'Per-job in-app notification-on-failure override. In-app twin of emailOnFailure. Default `inherit`.',
  },

  // MCP-only reference tags (#5932) — not part of the canonical contract.
  case: {
    type: 'string',
    description: 'Tracker case/ticket reference to tag the job with (e.g. "PH-52"), for later lookup via list filtering. Inert bookkeeping — stored, never triggers behavior.',
  },
  pr: {
    type: 'string',
    description: 'PR reference to tag the job with for later lookup. Inert bookkeeping. To instead have a PR test plan AI-consolidated, use prNumbers.',
  },
};
